import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'

import { useAdminQuery } from '../api'
import type { ScreenLink } from '../types'

/**
 * L'adresse de l'écran de la salle, prête à ouvrir sur la télé.
 *
 * La clé d'affichage ne vivait que dans le `.env` du serveur : le soir J, la
 * personne qui branche la télé devait la demander à qui avait accès au
 * serveur. Elle est servie ici à qui règle l'édition — et seulement à lui :
 * avec cette adresse, n'importe qui affiche le programme de la salle.
 */
export function ScreenLinkCard({ eventId }: { eventId: string | undefined }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const query = useAdminQuery<ScreenLink>(
    ['admin-screen-link', eventId],
    `/admin/events/${eventId}/screen-link`,
    eventId,
  )
  const link = query.data

  function copy(url: string) {
    // Le presse-papiers exige une page sûre (https) ; à défaut, le champ
    // reste sélectionnable à la main.
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => setCopied(false))
  }

  return (
    <Card className="space-y-3">
      <div>
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.screenLink.title')}
        </h2>
        <p className="mt-1 text-sm text-content-secondary">{t('admin.screenLink.hint')}</p>
      </div>

      {link && !link.configured ? (
        <p className="text-sm text-state-warning">{t('admin.screenLink.notConfigured')}</p>
      ) : null}

      {link?.url ? (
        <>
          <input
            readOnly
            value={link.url}
            onFocus={(event) => event.currentTarget.select()}
            aria-label={t('admin.screenLink.title')}
            className="w-full rounded-md border border-line bg-bg-base px-3 py-2.5 font-mono text-xs text-content-primary"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => copy(link.url!)}>
              {copied ? t('admin.screenLink.copied') : t('admin.screenLink.copy')}
            </Button>
            <a
              href={link.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-champagne-300 underline"
            >
              {t('admin.screenLink.open')}
            </a>
          </div>
          <p className="text-caption text-state-warning">{t('admin.screenLink.secret')}</p>
        </>
      ) : null}

      {query.isError ? <p className="text-sm text-state-danger">{query.error.message}</p> : null}
    </Card>
  )
}
